import React, { FC, useEffect, useRef } from 'react';
import { useAlert, AlertProps } from '../../core';
import { i18n } from '../../i18n/translate';

export type AlertMessageType = 'danger' | 'warning' | 'success';
export interface Props {
  clearMessages?: () => void;
  manualDismiss?: boolean;
  message: string;
  messageType?: AlertMessageType;
  onAlertDismiss?: () => void;
  timeout?: boolean;
  visible?: boolean;
}

export const AlertMessage: FC<Props> = ({
  clearMessages = () => {},
  manualDismiss,
  message,
  messageType = 'success',
  onAlertDismiss = () => {},
  timeout = true,
  visible = true,
}) => {
  const alertShownRef = useRef(false);
  const previousMessageRef = useRef<string>('');

  const getVariant = (type: AlertMessageType): AlertProps['variant'] => {
    switch (type) {
      case 'danger':
        return 'error';
      case 'warning':
        return 'warning';
      case 'success':
      default:
        return 'success';
    }
  };

  const getTitle = (type: AlertMessageType): string => {
    switch (type) {
      case 'danger':
        return i18n.t('Error');
      case 'warning':
        return i18n.t('Warning');
      case 'success':
      default:
        return i18n.t('Success');
    }
  };

  const duration = manualDismiss ? null : timeout ? 4000 : undefined;
  const showAlert = useAlert(getVariant(messageType), 'toast', 'top', duration);

  useEffect(() => {
    if (!message || !visible) return;

    // trying to avoid showing the same alert again
    if (message === previousMessageRef.current && alertShownRef.current) return;

    const id = Date.now().toString(); // unique id per alert

    showAlert({
      id,
      title: getTitle(messageType),
      description: message,
      isClosable: true,
      showIcon: true,
      onClose: () => {
        clearMessages();
        onAlertDismiss();
        alertShownRef.current = false;
        previousMessageRef.current = '';
      },
    });

    alertShownRef.current = true;
    previousMessageRef.current = message;
  }, [message, visible, manualDismiss, timeout, showAlert, clearMessages, onAlertDismiss, messageType]);

  return null;
};

AlertMessage.displayName = 'AlertMessage';
